import { useState } from "react";
import { Link } from "react-router-dom";
import { Twitter, Facebook, Youtube, Instagram, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { SECTIONS, useLang } from "../lib/i18n";
import { LOGO_URL } from "./Masthead";

// Asli social profile links yahan daalein — khaali hone par icon chhup jaata hai
const SOCIALS = [
  { Icon: Twitter, url: "", label: "X (Twitter)" },
  { Icon: Facebook, url: "", label: "Facebook" },
  { Icon: Youtube, url: "", label: "YouTube" },
  { Icon: Instagram, url: "", label: "Instagram" },
];

const INFO_LINKS = [
  { slug: "about", hi: "हमारे बारे में", en: "About Us" },
  { slug: "contact", hi: "संपर्क", en: "Contact" },
  { slug: "privacy", hi: "प्राइवेसी पॉलिसी", en: "Privacy Policy" },
  { slug: "terms", hi: "नियम व शर्तें", en: "Terms & Conditions" },
  { slug: "corrections", hi: "सुधार नीति", en: "Corrections Policy" },
  { slug: "disclaimer", hi: "डिस्क्लेमर", en: "Disclaimer" },
  { slug: "grievance", hi: "शिकायत अधिकारी", en: "Grievance Officer" },
  { slug: "refund", hi: "रिफंड नीति", en: "Refund Policy" },
];

function WhatsAppSubscribe() {
  const { lang } = useLang();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/subscribe", { phone: phone.trim() });
      setPhone("");
      toast.success(
        lang === "hi"
          ? `अलर्ट चालू हो गए! ब्रेकिंग ख़बरें ${data.phone} पर मिलेंगी`
          : `Alerts on! Breaking news will reach ${data.phone}`
      );
    } catch (err) {
      toast.error(apiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="border-y border-gold/25">
      <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
        <div className="flex items-start gap-4">
          <span className="w-11 h-11 shrink-0 rounded-full bg-gold flex items-center justify-center text-deepgreen">
            <MessageCircle size={20} />
          </span>
          <div>
            <h4 className="font-hindi text-xl text-gold-light">
              {lang === "hi" ? "ब्रेकिंग न्यूज़ अलर्ट — सीधे WhatsApp पर" : "Breaking news alerts — straight to WhatsApp"}
            </h4>
            <p className="text-sm text-paper/70 mt-1">
              {lang === "hi"
                ? "अपना नंबर डालें — जैसे ही कोई बड़ी ख़बर प्रकाशित होगी, सबसे पहले आपको पता चलेगा।"
                : "Drop your number — the moment a big story breaks, you hear it first."}
            </p>
          </div>
        </div>
        <form onSubmit={submit} className="flex w-full md:w-auto" data-testid="whatsapp-subscribe-form">
          <input
            data-testid="whatsapp-subscribe-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 XXXXX XXXXX"
            className="w-full md:w-64 bg-deepgreen-hover border border-gold/40 text-paper px-4 py-2.5 text-sm outline-none focus:border-gold placeholder:text-paper/40"
          />
          <button
            data-testid="whatsapp-subscribe-button"
            type="submit"
            disabled={busy}
            className="shrink-0 bg-gold text-deepgreen px-5 py-2.5 text-sm font-bold hover:bg-gold-light transition-colors disabled:opacity-60"
          >
            {busy ? "…" : lang === "hi" ? "अलर्ट चालू करें" : "Get alerts"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Footer() {
  const { lang, t } = useLang();
  return (
    <footer data-testid="footer" className="mt-16 bg-deepgreen text-paper/90 border-t-4 border-gold">
      <div className="max-w-7xl mx-auto px-4 py-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <img src={LOGO_URL} className="h-16 w-auto" alt="The Vainor India" />
          <p className="font-cinzel text-sm font-extrabold tracking-[0.22em] text-gold-light mt-3">
            THE VAINOR INDIA
          </p>
          <p className="mt-3 text-sm text-paper/70 leading-relaxed">
            {lang === "hi"
              ? "द वैनोर इंडिया — जनता की आवाज़। सत्य, निष्पक्षता और निर्भीकता के साथ हर ख़बर, सबसे पहले।"
              : "The Vainor India — the voice of the people. Every story, first, with truth, integrity and courage."}
          </p>
          <div className="flex gap-3 mt-5">
            {SOCIALS.filter((s) => s.url).map(({ Icon, url, label }, i) => (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid={`social-link-${i}`}
                aria-label={label}
                className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold hover:text-deepgreen transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">
            {lang === "hi" ? "अनुभाग" : "Sections"}
          </h4>
          <div className="grid grid-cols-1 gap-2.5">
            {SECTIONS.slice(0, 6).map((s) => (
              <Link
                key={s.id}
                to={`/section/${s.id}`}
                data-testid={`footer-${s.id}`}
                className="text-sm text-paper/75 hover:text-gold transition-colors"
              >
                {lang === "hi" ? s.hi : s.en}
              </Link>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">
            {lang === "hi" ? "जानकारी" : "Information"}
          </h4>
          <div className="grid grid-cols-1 gap-2.5">
            {INFO_LINKS.map((p) => (
              <Link
                key={p.slug}
                to={`/p/${p.slug}`}
                data-testid={`footer-info-${p.slug}`}
                className="text-sm text-paper/75 hover:text-gold transition-colors"
              >
                {lang === "hi" ? p.hi : p.en}
              </Link>
            ))}
          </div>
        </div>
        <div className="md:col-span-4">
          <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">
            {lang === "hi" ? "मालिक एवं प्रकाशक" : "Owner & Publisher"}
          </h4>
          <p className="text-sm text-paper/80 leading-relaxed" data-testid="footer-imprint">
            <span className="font-semibold text-gold-light">THE VAINOR VENTURES</span>
            {lang === "hi" ? " (प्रोपराइटरशिप फर्म)" : " (Proprietorship Firm)"}
            <br />
            {lang === "hi" ? "प्रोपराइटर" : "Proprietor"}: Lokendra Singh
            <br />
            GSTIN: 08KVFPS4146R1ZH
            <br />
            MSME/Udyam Reg. No.: UDYAM-RJ-18-0024177
            <br />
            Baiya, Fatehgarh, Jaisalmer, Rajasthan – 345027
            <br />
            <a href="mailto:Thevainorventures@gmail.com" className="hover:text-gold transition-colors">
              Thevainorventures@gmail.com
            </a>
            {" • "}
            <a href="tel:+919521018646" className="hover:text-gold transition-colors">
              +91 95210 18646
            </a>
          </p>
        </div>
      </div>

      <WhatsAppSubscribe />

      <div className="border-t border-gold/25">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-xs font-mono text-paper/60">
          <span>
            © 2026 The Vainor India — {lang === "hi" ? "एक The Vainor Ventures फर्म। सर्वाधिकार सुरक्षित।" : "A The Vainor Ventures firm. All rights reserved."}
          </span>
          <span className="text-gold/80">{t.tagline}</span>
        </div>
      </div>
    </footer>
  );
}
